import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import cn from "classnames";

import styles from "./AlertModal.module.scss";

type Props = React.ComponentProps<typeof AlertDialogPrimitive.Root> & {
  onAfterOpen?: () => void;
  onRequestClose?: () => void;
};

/**
 * AlertModal Components
 *
 * These components provide a flexible and accessible alert modal implementation using Radix UI's AlertDialog primitives.
 *
 * Usage:
 * - Wrap your alert modal logic with the `AlertModal` (AlertModalRoot) component.
 * - Place an `AlertModalTrigger` inside the `AlertModal` to open the modal.
 * - Place an `AlertModalContent` inside the `AlertModal` to render the modal's content.
 * - Use `AlertModalTitle`, `AlertModalDescription`, `AlertModalAction`, and `AlertModalCancel` for enhanced accessibility and UI.
 * - The open/closed state of the modal can be controlled externally by passing the `open` prop to the root alert modal component (`AlertModal`) and using the onAfterOpen/onRequestClose callbacks.
 * - If not provided, the modal manages its own open/close logic automatically.
 *
 * Important:
 * - Both a trigger (`AlertModalTrigger`) and content (`AlertModalContent`) must be used within the `AlertModal` root for the modal to function correctly.
 * - By default, clicking outside the alert modal content will NOT close the modal. Use this for critical alerts or confirmations.
 * - Therefore, the Action and/or Cancel components must also be used if you want to provide a way for users to close the modal.
 *
 * Example:
 * ```
 * <AlertModal>
 *   <AlertModalTrigger>Open Alert</AlertModalTrigger>
 *   <AlertModalContent>
 *     <AlertModalTitle>Title</AlertModalTitle>
 *     <AlertModalDescription>Description</AlertModalDescription>
 *     <AlertModalAction>Confirm</AlertModalAction>
 *     <AlertModalCancel>Cancel</AlertModalCancel>
 *   </AlertModalContent>
 * </AlertModal>
 * ```
 *
 * @component AlertModal
 * @description
 * Root component for the alert modal dialog. Wraps Radix AlertDialogPrimitive.Root and manages open/close events.
 * Use this as the parent for all alert modal-related components.
 *
 * @prop {() => void} [onAfterOpen] - Callback fired after the alert modal opens.
 * @prop {() => void} [onRequestClose] - Callback fired after the alert modal closes.
 * @note To pass custom children to the trigger, title, description, or cancel/action button, use the `asChild` prop.
 */
const AlertModalRoot = ({
  children,
  onAfterOpen,
  onRequestClose,
  ...props
}: Props) => {
  const handleOpenChange = (open: boolean) => {
    if (open) {
      onAfterOpen?.();
    }
    if (!open) {
      onRequestClose?.();
    }
  };

  return (
    <AlertDialogPrimitive.Root onOpenChange={handleOpenChange} {...props}>
      {children}
    </AlertDialogPrimitive.Root>
  );
};

/**
 * @component AlertModalTrigger
 * @description
 * Wrapper for the alert modal trigger button. Use this to open the alert modal.
 * Pass `asChild` to render a custom trigger element.
 */
const AlertModalTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Trigger>
>(({ ...props }, ref) => <AlertDialogPrimitive.Trigger ref={ref} {...props} />);
AlertModalTrigger.displayName = "AlertModalTrigger";

/**
 * @component AlertModalContent
 * @description
 * Wrapper for the alert modal content. Renders the alert modal overlay and content area.
 */
const AlertModalContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Portal>
    <AlertDialogPrimitive.Overlay
      className={cn(styles.overlay)}
      data-testid="modal-overlay"
    />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(styles.content, className)}
      {...props}
    />
  </AlertDialogPrimitive.Portal>
));
AlertModalContent.displayName = "AlertModalContent";

/**
 * @component AlertModalTitle
 * @description
 * Wrapper for the alert modal title. Use for accessible alert modal headings.
 * Pass `asChild` to render a custom title element.
 */
const AlertModalTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Title>
>(({ ...props }, ref) => <AlertDialogPrimitive.Title ref={ref} {...props} />);
AlertModalTitle.displayName = "AlertModalTitle";

/**
 * @component AlertModalDescription
 * @description
 * Wrapper for the alert modal description. Use for accessible alert modal descriptions.
 * Pass `asChild` to render a custom description element.
 */
const AlertModalDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Description>
>(({ ...props }, ref) => (
  <AlertDialogPrimitive.Description ref={ref} {...props} />
));
AlertModalDescription.displayName = "AlertModalDescription";

/**
 * @component AlertModalAction
 * @description
 * Wrapper for the alert modal action button. Use for the primary action (e.g., confirm).
 * Pass `asChild` to render a custom action element.
 */
const AlertModalAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Action>
>(({ ...props }, ref) => <AlertDialogPrimitive.Action ref={ref} {...props} />);
AlertModalAction.displayName = "AlertModalAction";

/**
 * @component AlertModalCancel
 * @description
 * Wrapper for the alert modal cancel button. Use for the secondary action (e.g., cancel).
 * Pass `asChild` to render a custom cancel element.
 */
const AlertModalCancel = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof AlertDialogPrimitive.Cancel>
>(({ ...props }, ref) => <AlertDialogPrimitive.Cancel ref={ref} {...props} />);
AlertModalCancel.displayName = "AlertModalCancel";

export {
  AlertModalRoot as AlertModal,
  AlertModalTrigger,
  AlertModalContent,
  AlertModalTitle,
  AlertModalDescription,
  AlertModalAction,
  AlertModalCancel,
};
